import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';
import isNil from 'lodash/isNil';

import Collection from '@/collection/entity/Collection';
import { useCollectionsStore } from '@/collection/stores/CollectionsStore';
import Button from '@/common/components/Button/Button';
import Icon from '@/common/components/Icon/Icon';
import TextField from '@/common/components/inputs/TextField/TextField';
import Loader from '@/common/components/Loader/Loader';
import { ProductEntity } from '@/product/types';

import CollectionsList from '../CollectionsList/CollectionsList';

import styles from './CollectionPopover.module.css';

interface CollectionPopoverProps {
  product?: Nullable<ProductEntity>;
  className?: string;
  onItemSelection?: Nullable<(collection: Collection) => void>;
  withLinks?: boolean;
  onClose?: () => void;
}

function CollectionPopover(props: CollectionPopoverProps) {
  const { className = '', onItemSelection = null, product = null, withLinks = false, onClose } = props;

  const { activeCollection, collections, createNewCollection } = useCollectionsStore();

  const [collectionTitle, setCollectionTitle] = useState('');
  const [searchString, setSearchString] = useState('');

  const filteredCollections = useMemo((): Nullable<Collection[]> => {
    if (!searchString || !collections) {
      return collections;
    }
    const search = searchString.toLowerCase();

    return collections.filter((collection) => collection.title && collection.title.toLowerCase().includes(search));
  }, [searchString, collections]);

  const handleCollectionCreation = useCallback(() => {
    createNewCollection(collectionTitle).then(() => {
      setCollectionTitle('');

      if (onItemSelection && activeCollection) {
        onItemSelection(activeCollection);
      }
    });

    if (product !== null) {
      onClose?.();
    }
  }, [activeCollection, collectionTitle, createNewCollection, onItemSelection, onClose, product]);

  const handleNewCollectionTitleChanging = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCollectionTitle(e.target.value);
  }, []);

  const handleSearchStringChanging = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
  }, []);

  return (
    <div
      className={cls(
        styles.collectionsPopover,
        product ? styles.collectionsPopoverSmall : styles.collectionsPopoverFull,
        className,
      )}
      aria-label="testaaa"
    >
      <div className={styles.collectionsPopoverSearch}>
        <Icon name="search" className={styles.collectionsPopoverSearchIcon} />

        <TextField
          aria-label="Search collections"
          placeholder="Search"
          className={styles.collectionsPopoverSearchInput}
          name="collection-search"
          modifier="squared"
          value={searchString}
          onChange={handleSearchStringChanging}
        />
      </div>

      {isNil(filteredCollections) && <Loader className={styles.collectionsPopoverLoader} />}

      {filteredCollections && filteredCollections?.length > 0 && (
        <div className={styles.collectionsPopoverCollections}>
          <CollectionsList
            withLinks={withLinks}
            onLinkClick={onClose}
            onItemClick={onItemSelection}
            collections={filteredCollections}
            productContext={product}
          />
        </div>
      )}

      {filteredCollections && filteredCollections.length === 0 && (
        <div className={styles.collectionsPopoverEmpty}>No Collections found, please create one</div>
      )}

      <TextField
        aria-label="New collection title"
        autoComplete="off"
        placeholder="Type here collection title…"
        name="title"
        modifier="white"
        value={collectionTitle}
        onChange={handleNewCollectionTitleChanging}
      />

      <Button
        aria-label="Create new Collection"
        block
        color="primary"
        disabled={!filteredCollections || collectionTitle === ''}
        onClick={handleCollectionCreation}
      >
        Create new Collection
      </Button>
    </div>
  );
}

export default observer(CollectionPopover);
